import { Alert, AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, ListItem, ListItemButton, Switch, Tooltip, Typography, styled } from "@mui/material";
import FormSubmitButton from "@/components/forms/FormSubmitButton";
import Form from "@/components/forms/Form";
import Select, { ISelectItem } from "@/components/forms/inputs/Select";
import { ConfigurationSourceData, ConfigurationSourceDataAllocation, Institution, OpenClinicaStudyEventStatus, Participant, ParticipantSourceDataAllocationUpdate, PostSetOpenClinicaStudyEventStatus, SourceDataEvent } from "@/api/dtos";
import { IReturn } from "@servicestack/client";
import { GroupedVirtuoso } from "react-virtuoso";
import { ComponentProps, FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import InputOnlyField from "@/components/forms/fields/inputOnly/InputOnlyField";
import Checkbox from "@/components/forms/inputs/Checkbox";
import { DateTime } from "luxon";
import { IFormSubmit, IFormSubmitFailed, InputComponentType, OmitInputProps, useFormActions, useScopedField } from "@ngt/forms-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-duotone-svg-icons";
import { FormError } from "@/errors";
import { AppSettings } from "@/AppSettings";
import { DefaultSourceDataFormSettings, ISourceDataFormSettings } from "@/contexts/form/SourceDataFormSettingsContext";
import ServiceStackClientContext from "@/contexts/api/ServiceStackClientContext";
import { useSnackbar } from "notistack";


const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogTitle-root': {
        padding: theme.spacing(2),
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'space-between',
        alignItems: 'center',

        '& > div': {
            display: 'flex',
            alignItems: 'center'
        }
    },
    '& .MuiDialogContent-root': {
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
    margin: theme.spacing(0,-3),
}));


const FormButtons = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end'
}));

interface ISourceDataEventEditDialogProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    sourceDataEvent?: SourceDataEvent;
    open?: boolean;
    onClose?: () => void;
    onSave?: () => Promise<any>;
}

const buttonModeOptions = [
    {
        id: 'compact',
        name: 'Compact'
    },
    {
        id: 'separate',
        name: 'Expanded'
    }
]

const subjectEventStatusOptions = [
    // {
    //     id: OpenClinicaStudyEventStatus.NotScheduled,
    //     name: 'Not Scheduled',
    //     description: `The event has not yet been scheduled in OpenClinica.`,
    //     disabled: true
    // },
    // {
    //     id: OpenClinicaStudyEventStatus.Scheduled,
    //     name: 'Scheduled',
    //     description: `The event has been scheduled in OpenClinica.`,
    //     disabled: true
    // },
    {
        id: OpenClinicaStudyEventStatus.DataEntryStarted,
        name: 'Data Entry Started',
        description: `The event has started data entry in OpenClinica.`
    },
    {
        id: OpenClinicaStudyEventStatus.Completed,
        name: 'Completed',
        description: `The event has completed data entry in OpenClinica.`
    },
    {
        id: OpenClinicaStudyEventStatus.Locked,
        name: 'Locked',
        description: `The event has been locked to data entry in OpenClinica.`
    },
    {
        id: OpenClinicaStudyEventStatus.Stopped,
        name: 'Stopped',
        description: `The event has stopped data entry in OpenClinica.`
    },
    {
        id: OpenClinicaStudyEventStatus.Skipped,
        name: 'Skipped',
        description: `The event has been skipped data entry in OpenClinica.`
    },
    // {
    //     id: OpenClinicaStudyEventStatus.Signed,
    //     name: 'Signed',
    //     description: `The event has been signed off in OpenClinica.`,
    //     disabled: true
    // }
]

const SourceDataEventEditDialog: FC<ISourceDataEventEditDialogProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    sourceDataEvent,
    open,
    onClose,
    onSave
}) => {
    const client = useContext(ServiceStackClientContext);
    const { enqueueSnackbar } = useSnackbar();

    const submit = useCallback<IFormSubmit<SourceDataEvent, FormError>>(async (formState, formActions) => {
        
        await client.post(new PostSetOpenClinicaStudyEventStatus({
            trialId,
            masterGroupId,
            coordinatingGroupId,
            institutionId,
            participantId,
            studyEventOid,
            studyEventRepeat,
            openClinicaStudyEventId: formState.value?.openClinicaStudyEventId,
            openClinicaStudyEventStatus: formState.value?.openClinicaStudyEventStatus
        }));

        await onSave?.();

        onClose?.();

        enqueueSnackbar(`OpenClinica event successfully updated!`, { variant: "success" });

        return {};
    }, [onSave, onClose, client, enqueueSnackbar]);

    const submitFailed = useCallback<IFormSubmitFailed<SourceDataEvent, FormError>>(async (formState, formActions) => {
        
        enqueueSnackbar(`An error occurred while attempting to update the OpenClinica event!`, { variant: "error" });

        return {};
    }, [enqueueSnackbar]);

    const allowed = useMemo(() => {
        return subjectEventStatusOptions.some(x => x.id === sourceDataEvent?.openClinicaStudyEventStatus)
    }, [sourceDataEvent?.openClinicaStudyEventStatus])

    return (
        <Form
            initialValue={sourceDataEvent}
            onSubmit={submit}
            onSubmitFailed={submitFailed}
            debug={AppSettings.debug}
        >
            <StyledDialog
                //onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open ?? false}
                maxWidth="sm"
                fullWidth
                scroll="paper"
            >
                <DialogTitle>
                    Update OpenClinica Event
                </DialogTitle>
                {
                    !allowed && (
                        <>
                            <DialogContent dividers>
                                <Alert
                                    severity="warning"
                                >
                                    <AlertTitle>
                                        Unsupported OpenClinica Event Status
                                    </AlertTitle>
                                    <Typography>
                                        The SDV only supports updating OpenClinica events in one of the following statuses:
                                        <ul>
                                            <li>
                                                Data Entry Started
                                            </li>
                                            <li>
                                                Completed
                                            </li>
                                            <li>
                                                Locked
                                            </li>
                                            <li>
                                                Stopped
                                            </li>
                                            <li>
                                                Skipped
                                            </li>
                                        </ul>
                                    </Typography>
                                </Alert>
                            </DialogContent>
                            <DialogActions>
                                    <FormButtons>
                                        { 
                                            onClose && (
                                                <Button
                                                    variant="contained"
                                                    color="inherit"
                                                    onClick={onClose}
                                                >
                                                    Cancel
                                                </Button>
                                            )
                                        }
                                    </FormButtons>
                            </DialogActions>
                        </>
                    )
                }
                {
                    allowed && (
                        <>
                            <DialogContent dividers>
                                <InputOnlyField
                                    propertyPath="openClinicaStudyEventStatus"
                                    label="Status"
                                    component={Select}
                                    options={subjectEventStatusOptions}
                                    nullOption={false}
                                    sx={{
                                        mt: 2,
                                        mb: 3
                                    }}
                                />
                            </DialogContent>
                            <DialogActions>
                                    <FormButtons>
                                        <FormSubmitButton
                                            variant="contained"
                                            color="primary"
                                        >
                                            Ok
                                        </FormSubmitButton>
                                        { 
                                            onClose && (
                                                <Button
                                                    variant="contained"
                                                    color="inherit"
                                                    onClick={onClose}
                                                >
                                                    Cancel
                                                </Button>
                                            )
                                        }
                                    </FormButtons>
                            </DialogActions>
                        </>
                    )
                }
            </StyledDialog>
        </Form>
    );
}

export default SourceDataEventEditDialog;